// ============================================================
// wheel — pure helpers for the spin-to-win step
// ============================================================
//
// Shared by the widget (WheelStage + step routing), the editor mirror and
// the submissions API (server-side outcome). NO React, NO "@/" alias, NO
// browser globals: api/popup-submissions.ts imports this file directly and
// Vercel's function bundler only resolves plain relative paths.

import type {
  PopupContentSettings,
  PopupWheelContent,
  PopupWheelSegment,
  PopupWheelSpinResult,
  PopupWheelFormPlacement,
} from "../shared/types/popup-types";

/** Default wedge fills — a warm, high-contrast cycle that reads on light and
 *  dark panels alike. Segments without their own colour cycle through it. */
export const WHEEL_DEFAULT_PALETTE = [
  "#f97316",
  "#111827",
  "#facc15",
  "#0ea5e9",
  "#ef4444",
  "#22c55e",
  "#a855f7",
  "#f472b6",
];

export const WHEEL_MIN_SEGMENTS = 2;
export const WHEEL_MAX_SEGMENTS = 12;

export const WHEEL_DEFAULTS = {
  size: 340,
  border_width: 8,
  spin_duration_ms: 5500,
  min_rotations: 6,
  result_delay_ms: 1200,
  result_heading: "You won {prize}!",
  lose_heading: "Better luck next time!",
  countdown_label: "Your code expires in {time}",
  button_text: "Spin the wheel",
  spinning_text: "Spinning…",
} as const;

const SLUG_RE = /^[a-zA-Z0-9_-]{1,64}$/;

/** A segment counts when it has a label AND a slug-safe id. Weight is
 *  clamped to a finite non-negative number. */
export function normalizeSegments(
  wheel: PopupWheelContent | null | undefined
): PopupWheelSegment[] {
  const list = Array.isArray(wheel?.segments) ? wheel!.segments : [];
  const out: PopupWheelSegment[] = [];
  const seen = new Set<string>();
  for (const raw of list) {
    if (!raw || typeof raw !== "object") continue;
    const label = String(raw.label ?? "").trim();
    const id = String(raw.id ?? "").trim();
    if (!label || !SLUG_RE.test(id) || seen.has(id)) continue;
    seen.add(id);
    const w = Number(raw.weight);
    out.push({
      ...raw,
      id,
      label,
      weight: Number.isFinite(w) && w > 0 ? w : 0,
      prize_type: raw.prize_type || "custom",
    });
    if (out.length >= WHEEL_MAX_SEGMENTS) break;
  }
  return out;
}

/** Playable = enabled, at least two valid wedges, and SOMETHING can land. */
export function wheelPlayable(
  content: PopupContentSettings | null | undefined
): boolean {
  const wheel = content?.wheel;
  if (!wheel?.enabled) return false;
  const segs = normalizeSegments(wheel);
  if (segs.length < WHEEL_MIN_SEGMENTS) return false;
  return segs.some((s) => s.weight > 0);
}

export function wheelFormPlacement(
  content: PopupContentSettings | null | undefined
): PopupWheelFormPlacement {
  const p = content?.wheel?.form_placement;
  return p === "before" || p === "after" ? p : "inline";
}

/** A wedge "wins" when it hands the visitor anything at all. */
export function segmentIsWin(seg: PopupWheelSegment): boolean {
  return seg.prize_type !== "none";
}

/**
 * Weighted pick. `rand` returns [0, 1). Zero-weight wedges never land; a
 * wheel whose weights all sum to 0 is not playable (guarded upstream), but
 * fall back to the first wedge rather than throwing.
 */
export function pickSegment(
  segments: PopupWheelSegment[],
  rand: () => number = Math.random
): number {
  const total = segments.reduce((acc, s) => acc + (s.weight > 0 ? s.weight : 0), 0);
  if (total <= 0) return 0;
  let r = rand() * total;
  for (let i = 0; i < segments.length; i++) {
    const w = segments[i].weight > 0 ? segments[i].weight : 0;
    if (w === 0) continue;
    if (r < w) return i;
    r -= w;
  }
  // Floating-point tail: land on the last weighted wedge.
  for (let i = segments.length - 1; i >= 0; i--) if (segments[i].weight > 0) return i;
  return 0;
}

/** Odds per wedge as a fraction of 1 (0 for zero-weight wedges). */
export function segmentOdds(segments: PopupWheelSegment[]): number[] {
  const total = segments.reduce((acc, s) => acc + (s.weight > 0 ? s.weight : 0), 0);
  return segments.map((s) => (total > 0 && s.weight > 0 ? s.weight / total : 0));
}

export function spinResultFor(
  segments: PopupWheelSegment[],
  index: number
): PopupWheelSpinResult {
  const seg = segments[index];
  return {
    segment_id: seg.id,
    index,
    label: seg.label,
    prize_type: seg.prize_type,
    ...(typeof seg.value === "number" ? { value: seg.value } : {}),
    ...(seg.code && seg.code.trim() ? { code: seg.code.trim() } : {}),
    win: segmentIsWin(seg),
    ...(seg.win_text && seg.win_text.trim() ? { win_text: seg.win_text.trim() } : {}),
  };
}

// ---------- geometry ----------

/** Degrees the pointer sits at, measured clockwise from 12 o'clock. */
export function pointerAngle(position: PopupWheelContent["pointer_position"]): number {
  if (position === "right") return 90;
  if (position === "left") return 270;
  return 0;
}

/** Wedge i spans [i*span, (i+1)*span) clockwise from 12 o'clock at rotation 0. */
export function segmentSpan(count: number): number {
  return 360 / Math.max(count, 1);
}

/**
 * The rotation (deg, clockwise) that parks wedge `index` under the pointer,
 * chosen ≥ `current + 360 * minRotations` so the wheel always keeps turning
 * forward. `jitter` in [-0.5, 0.5) nudges the landing off dead-centre by up
 * to ±42% of a wedge, so repeated spins never look scripted.
 */
export function targetRotation(
  current: number,
  index: number,
  count: number,
  minRotations: number,
  pointer: PopupWheelContent["pointer_position"],
  jitter = 0
): number {
  const span = segmentSpan(count);
  const centre = index * span + span / 2;
  const offset = Math.max(-0.42, Math.min(0.42, jitter)) * span;
  // Wedge centre must end up AT the pointer: (centre + rot) ≡ pointer (mod 360)
  const landing = ((pointerAngle(pointer) - centre - offset) % 360 + 360) % 360;
  const floor = current + 360 * Math.max(0, minRotations);
  const turns = Math.ceil((floor - landing) / 360);
  return landing + 360 * Math.max(turns, 0);
}

/** Which wedge sits under the pointer for a given rotation. */
export function segmentAtRotation(
  rotation: number,
  count: number,
  pointer: PopupWheelContent["pointer_position"]
): number {
  const span = segmentSpan(count);
  const angle = ((pointerAngle(pointer) - rotation) % 360 + 360) % 360;
  return Math.min(count - 1, Math.floor(angle / span));
}

/** Rotation angle (deg, clockwise) encoded in a CSS transform matrix string. */
export function rotationFromMatrix(transform: string | null | undefined): number {
  if (!transform || transform === "none") return 0;
  const m = transform.match(/matrix\(([^)]+)\)/);
  if (!m) return 0;
  const parts = m[1].split(",").map((p) => parseFloat(p.trim()));
  if (parts.length < 2 || parts.some((n) => Number.isNaN(n))) return 0;
  const deg = (Math.atan2(parts[1], parts[0]) * 180) / Math.PI;
  return ((deg % 360) + 360) % 360;
}

/** SVG path for a wedge from `start` to `end` degrees (clockwise from top). */
export function wedgePath(cx: number, cy: number, r: number, start: number, end: number): string {
  const toXY = (deg: number) => {
    const rad = ((deg - 90) * Math.PI) / 180;
    return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)];
  };
  const [sx, sy] = toXY(start);
  const [ex, ey] = toXY(end);
  const large = end - start > 180 ? 1 : 0;
  return `M ${cx} ${cy} L ${sx.toFixed(3)} ${sy.toFixed(3)} A ${r} ${r} 0 ${large} 1 ${ex.toFixed(3)} ${ey.toFixed(3)} Z`;
}

/** Point on the rim at `deg` clockwise from top. */
export function polar(cx: number, cy: number, r: number, deg: number): [number, number] {
  const rad = ((deg - 90) * Math.PI) / 180;
  return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)];
}

// ---------- colour helpers ----------

function channels(color: string): { r: number; g: number; b: number } | null {
  const c = (color || "").trim();
  const hex = c.match(/^#([0-9a-f]{6}|[0-9a-f]{3})$/i);
  if (hex) {
    let h = hex[1];
    if (h.length === 3) h = h.split("").map((x) => x + x).join("");
    return {
      r: parseInt(h.slice(0, 2), 16),
      g: parseInt(h.slice(2, 4), 16),
      b: parseInt(h.slice(4, 6), 16),
    };
  }
  const rgb = c.match(/^rgba?\(([^)]+)\)$/i);
  if (rgb) {
    const p = rgb[1].split(",").map((n) => parseFloat(n));
    if (p.length >= 3 && p.slice(0, 3).every((n) => !Number.isNaN(n))) {
      return { r: p[0], g: p[1], b: p[2] };
    }
  }
  return null;
}

/** Black-or-white label colour for a wedge fill. Unparseable = white. */
export function contrastText(fill: string): string {
  const c = channels(fill);
  if (!c) return "#ffffff";
  const lum = (0.299 * c.r + 0.587 * c.g + 0.114 * c.b) / 255;
  return lum > 0.62 ? "#111827" : "#ffffff";
}

export function segmentFill(
  seg: PopupWheelSegment,
  index: number,
  palette: string[] | undefined
): string {
  if (seg.color && seg.color.trim()) return seg.color.trim();
  const list = (palette && palette.length ? palette : WHEEL_DEFAULT_PALETTE).filter(
    (c) => typeof c === "string" && c.trim()
  );
  const cycle = list.length ? list : WHEEL_DEFAULT_PALETTE;
  // Avoid the same colour on the last and first wedge when the count divides
  // the palette unevenly by one — a wheel that reads as two identical
  // neighbours at 12 o'clock looks broken.
  return cycle[index % cycle.length];
}

/** Auto label size: dense wheels get smaller type. */
export function autoLabelFontSize(count: number, longest: number): number {
  let px = 22 - Math.max(0, count - 4) * 1.4;
  if (longest > 10) px -= (longest - 10) * 0.7;
  return Math.max(10, Math.min(22, Math.round(px)));
}

// ---------- copy ----------

export function resultHeading(
  wheel: PopupWheelContent | null | undefined,
  result: PopupWheelSpinResult
): string {
  if (result.win_text && result.win_text.trim()) return result.win_text.trim();
  if (!result.win) {
    return (wheel?.lose_heading || "").trim() || WHEEL_DEFAULTS.lose_heading;
  }
  const template = (wheel?.result_heading || "").trim() || WHEEL_DEFAULTS.result_heading;
  return template.replace(/\{prize\}/gi, result.label);
}

const PRIZE_TOKEN = /\{prize\}/i;

/** `{prize}` in a wheel popup's thank-you copy (heading, subheading, message,
 *  footer). A win fills in the wedge label. A loss turns a heading that names
 *  the prize into the losing headline and drops any other line that names
 *  it. With no spin yet (the editor's preview) the first prize wedge stands
 *  in. Copy without the token comes back untouched, so existing popups never
 *  change. */
export function fillPrizeToken(
  text: string | undefined,
  wheel: PopupWheelContent | null | undefined,
  result: PopupWheelSpinResult | null | undefined,
  role: "heading" | "line"
): string | undefined {
  if (!text || !PRIZE_TOKEN.test(text)) return text;
  if (result) {
    if (result.win) return text.replace(/\{prize\}/gi, result.label);
    return role === "heading" ? resultHeading(wheel, result) : "";
  }
  const sample =
    (wheel?.segments || []).find((s) => s && s.prize_type !== "none" && (s.label || "").trim())?.label ||
    "";
  return text.replace(/\{prize\}/gi, sample);
}

export function formatCountdown(msLeft: number): string {
  const s = Math.max(0, Math.floor(msLeft / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const mm = String(m).padStart(2, "0");
  const ss = String(sec).padStart(2, "0");
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

/** Where the success CTA sends a winner when auto-apply is on. Null = plain close. */
export function autoApplyUrl(
  wheel: PopupWheelContent | null | undefined,
  code: string | null | undefined,
  currentUrl: string
): string | null {
  const cfg = wheel?.auto_apply;
  if (!cfg?.enabled || !code) return null;
  const target = (cfg.url || "").trim() || currentUrl;
  if (cfg.platform === "shopify") {
    // Shopify's discount link applies the code and lands on `redirect`
    // (a path on the same store). Absolute targets fall back to the path.
    let path = "/";
    try {
      const u = new URL(target, currentUrl);
      path = u.pathname + u.search;
    } catch {
      path = "/";
    }
    return `/discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(path)}`;
  }
  if (cfg.platform === "woocommerce") {
    const sep = target.includes("?") ? "&" : "?";
    return `${target}${sep}coupon-code=${encodeURIComponent(code)}`;
  }
  const tpl = (cfg.url_template || "").trim();
  if (!tpl) return null;
  return tpl.replace(/\{code\}/gi, encodeURIComponent(code));
}
